import { isTruthy } from '@app/utils/isTruthy'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { convertFilterToQuery } from '@app/packages/announcementsFilter/actions'
import type { AnnouncementsFilter } from '@app/packages/announcementsFilter/AnnouncementsFilter'
import { asNetworkError } from '@app/packages/api/errors'
import {
  getDepersonalizedAnnouncementsByQuery,
  getDepersonalizedAnnouncementsSearch,
  getDepersonalizedAnnouncementsSearchCount,
  getDepersonalizedAnnouncementsSearchMap,
} from '@app/packages/api/v2/actions/depersonalized_announcements'
import type { AnnouncementSearchQuery } from '@app/packages/api/v2/types/params'
import { asError } from '@app/packages/asError/asError'

import { announcementsMetaSelector } from '@app/store/slices/announcements'
import {
  addDepersonalizedAnnouncementsPageSlice,
  depersonalizedAnnouncementsMetaSelector,
  failDepersonalizedAnnouncementsCountSlice,
  failDepersonalizedAnnouncementsSlice,
  setDepersonalizedAnnouncementsCountSlice,
  setDepersonalizedAnnouncementsMapSlice,
  setDepersonalizedAnnouncementsSlice,
  startDepersonalizedAnnouncementsCountSlice,
  startDepersonalizedAnnouncementsSlice,
} from '@app/store/slices/depersonalized_announcements'
import { createThunk } from '@app/store/thunk'

import type { PointsMapPoint } from '@app/components/PointsMap/PointsMapPoint'

import { getAnalyticsRouter } from './analytics'
import { callV2Api } from './api'

export const getDepersonalizedAnnouncementsInitial = () => {
  return createThunk(async (dispatch, getState) => {
    const request = dispatch(
      convertFilterToQuery({
        filter: announcementsMetaSelector(getState()).filter,
        page: 1,
        per_page: 25,
      })
    )

    return await dispatch(readDepersonalizedAnnouncements(request))
  })
}

export const getDepersonalizedAnnouncementsNext = () => {
  return createThunk(async (dispatch, getState) => {
    const { cursor, fetched } = depersonalizedAnnouncementsMetaSelector(getState())
    if (!cursor || fetched) return

    return await dispatch(readDepersonalizedAnnouncements(cursor))
  })
}

export const getDepersonalizedAnnouncementsMap = (filter: AnnouncementsFilter) => {
  return createThunk(async dispatch => {
    const per_page = 500
    const request = dispatch(convertFilterToQuery({ filter, page: 1, per_page }))

    let cursor = ''
    let points: PointsMapPoint[] = []

    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // Started loading the announcements behind the map. Measures the request, not a press.
        type: 'fetch_depersonalized_announcements_map.start',
      })).set('amplitude')
    )

    while (true) {
      const resp = await dispatch(callV2Api(getDepersonalizedAnnouncementsSearchMap(cursor ? cursor : request))).catch((e: unknown) => {
        const error = asError(e)
        dispatch(getAnalyticsRouter()).send(
          analyticsEvent(() => ({
            // Loading the map's announcements failed.
            type: 'fetch_depersonalized_announcements_map.error',
            data: { error: error.message, status: asNetworkError(error)?.status || 'unknown' },
          })).set('amplitude')
        )
        throw e
      })
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The map's announcements came back.
          type: 'fetch_depersonalized_announcements_map.fetch',
          data: { request },
        })).set('amplitude')
      )
      const page = toMapPoints(resp)

      // A request rather than a cursor means this is the first page, which replaces the map.
      dispatch(setDepersonalizedAnnouncementsMapSlice(page, { initial: !cursor }))
      points = points.concat(page)

      if (resp.data.length < per_page) return points
      cursor = resp.meta.cursor
    }
  })
}

export const fetchDepersonalizedAnnouncementsByIds = (ids: string[]) => {
  return createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(getDepersonalizedAnnouncementsByQuery({ 'filter[id]': ids })))
    dispatch(setDepersonalizedAnnouncementsSlice(resp))
    return resp
  })
}

export const countDepersonalizedAnnouncements = (request: AnnouncementSearchQuery) => {
  return createThunk(async dispatch => {
    dispatch(startDepersonalizedAnnouncementsCountSlice())
    try {
      const resp = await dispatch(callV2Api(getDepersonalizedAnnouncementsSearchCount(request)))
      dispatch(setDepersonalizedAnnouncementsCountSlice(resp.meta.total))
      return resp
    } catch (e) {
      dispatch(failDepersonalizedAnnouncementsCountSlice(asError(e)))
      throw e
    }
  })
}

/** The map reads two coordinates off each announcement's trimmed location; nothing else is kept. */
export const toMapPoints = (resp: {
  data: { id: string; relationships: { location: { data?: { id: string } | null } } }[]
  included?: { id: string; attributes: { rough_latitude: number; rough_longitude: number } }[]
}): PointsMapPoint[] => {
  const locations = new Map((resp.included ?? []).map(location => [location.id, location]))

  return resp.data
    .map((item): PointsMapPoint | null => {
      const id = item.relationships.location.data?.id
      const location = id ? locations.get(id) : undefined
      if (!location) return null
      return { id: item.id, coords: { lat: location.attributes.rough_latitude, lon: location.attributes.rough_longitude } }
    })
    .filter(isTruthy)
}

const readDepersonalizedAnnouncements = (request: AnnouncementSearchQuery | string) => {
  return createThunk(async dispatch => {
    dispatch(startDepersonalizedAnnouncementsSlice())
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // Started loading the announcement list. Measures the request, not a press.
        type: 'fetch_depersonalized_announcements.start',
      })).set('amplitude')
    )

    try {
      const resp = await dispatch(callV2Api(getDepersonalizedAnnouncementsSearch(request)))
      dispatch(addDepersonalizedAnnouncementsPageSlice(resp))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The announcement list came back.
          type: 'fetch_depersonalized_announcements.fetch',
          data: { request },
        })).set('amplitude')
      )
      return resp
    } catch (e) {
      const error = asError(e)
      dispatch(failDepersonalizedAnnouncementsSlice())
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // Loading the announcement list failed.
          type: 'fetch_depersonalized_announcements.error',
          data: { error: error.message, status: asNetworkError(error)?.status || 'unknown' },
        })).set('amplitude')
      )
      throw e
    }
  })
}
